import React, { useEffect, useState } from 'react';
import { Button, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';

import { Modal } from 'components/Modal';
import { DetailViewTabList } from 'components/TabList';
import { useExclusions } from '../api/getExclusions';
import { useLotRates } from '../api/getLotRates';
import { useRates } from '../api/getRates';
import { useRateSuppliers } from '../api/getRateSuppliers';
import { useUpdateRate } from '../api/updateRate';
import { useUpdateRatesPerSupplier } from '../api/updateRatesPerSupplier';
import { LotRatesMatrices } from './LotRatesMatrices';
import { RateInputMatrices } from './RateInputMatrices';
import { SupplierRatesEditor } from './SupplierRatesEditor';

const summaryTab = 'summary';

const countRates = (count) => (count === 1 ? '1 rate' : `${count} rates`);

const LotRatesSummary = ({ resource, id, lotId }) => {
  const { data, isFetchedAfterMount } = useLotRates(resource, id, lotId);

  return <LotRatesMatrices lotRates={data} isLoaded={isFetchedAfterMount} />;
};

export const RatesEditView = ({ resource, id, lot, onLotChanged, onModalChange }) => {
  const isAgreement = resource === 'framework_agreements';
  const perSupplier = isAgreement && lot.rates_per_supplier;
  const [confirmEnabled, setConfirmEnabled] = useState(null);
  const [isSwitching, setIsSwitching] = useState(false);
  const [tab, setTab] = useState(summaryTab);

  useEffect(() => {
    onModalChange(confirmEnabled !== null);
  }, [confirmEnabled, onModalChange]);

  const { data: exclusions } = useExclusions(resource, id, lot.lot_id);
  const { data: rates, refetch } = useRates(resource, id, lot.lot_id, { enabled: !perSupplier });
  const {
    data: suppliers,
    refetch: refetchSuppliers,
    isFetchedAfterMount: areSuppliersFetched,
  } = useRateSuppliers(id, lot.lot_id, { enabled: perSupplier });
  const { mutateAsync: updateRate } = useUpdateRate(resource, id, lot.lot_id);
  const { mutateAsync: updateRatesPerSupplier } = useUpdateRatesPerSupplier(id, lot.lot_id);

  const supplierRateCount = suppliers.reduce((count, { rate_count }) => count + rate_count, 0);
  const removedCount = (enabled) => (enabled ? rates.length : supplierRateCount);
  const supplier = perSupplier && suppliers.find(({ fac_id }) => fac_id === tab);

  const switchPerSupplier = (enabled) => {
    setIsSwitching(true);
    updateRatesPerSupplier({ enabled })
      .then(() => {
        setTab(summaryTab);
        return Promise.all([onLotChanged(), enabled ? refetchSuppliers() : refetch()]);
      })
      .catch(() => {})
      .finally(() => {
        setIsSwitching(false);
        setConfirmEnabled(null);
      });
  };

  return (
    <Stack gap={3}>
      <Stack gap={0.5}>
        {isAgreement && (
          <FormControlLabel
            sx={{ width: 'fit-content' }}
            control={
              <Checkbox
                checked={lot.rates_per_supplier}
                disabled={isSwitching}
                onChange={({ target: { checked } }) =>
                  removedCount(checked) === 0
                    ? switchPerSupplier(checked)
                    : setConfirmEnabled(checked)
                }
              />
            }
            label={<Typography fontWeight={700}>Rates per supplier</Typography>}
          />
        )}
        <Typography variant="body2" color="text.secondary">
          {perSupplier
            ? 'Set max rates in SEK/h for each supplier. The summary shows the range across suppliers.'
            : 'Set max rates in SEK/h. Use , or . for decimals. Each rate is saved when you leave the field.'}
        </Typography>
      </Stack>

      {!perSupplier && (
        <RateInputMatrices
          resource={resource}
          id={id}
          lotId={lot.lot_id}
          rates={rates}
          exclusions={exclusions}
          onSave={(roleId, levelId, rate) =>
            updateRate({ role_id: roleId, level_id: levelId, rate }).then(() => refetch())
          }
        />
      )}

      {perSupplier && (
        <Stack gap={2}>
          <DetailViewTabList
            multi
            value={supplier ? supplier.fac_id : summaryTab}
            onChange={(_, value) => setTab(value)}
            tabs={[
              { label: 'Summary', value: summaryTab },
              ...suppliers.map(({ fac_id, name, type, rate_count, total }) => ({
                label: type === 'group' ? `${name} (consortium group)` : name,
                value: fac_id,
                count: `${rate_count}/${total}`,
              })),
            ]}
          />
          {areSuppliersFetched && suppliers.length === 0 && (
            <Typography color="text.secondary">This lot has no suppliers yet.</Typography>
          )}
          {supplier ? (
            <SupplierRatesEditor
              key={supplier.fac_id}
              id={id}
              lot={lot}
              supplier={supplier}
              onSaved={refetchSuppliers}
            />
          ) : (
            <LotRatesSummary resource={resource} id={id} lotId={lot.lot_id} />
          )}
        </Stack>
      )}

      <Modal
        title="Rates per supplier"
        open={confirmEnabled !== null}
        handleClose={() => setConfirmEnabled(null)}
      >
        <Typography variant="body1" fontWeight={500}>
          {confirmEnabled
            ? `${countRates(removedCount(true))} set for all suppliers will be removed.`
            : `${countRates(removedCount(false))} set on suppliers will be removed.`}
        </Typography>
        <Stack direction="row" justifyContent="center" gap={1}>
          <Button variant="outlined" onClick={() => setConfirmEnabled(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={isSwitching}
            onClick={() => switchPerSupplier(confirmEnabled)}
          >
            Confirm
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
};
